import { ChevronDown, ChevronUp } from 'react-feather';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';

import { GetEventRegistrationsStatsQuery as GetEventRegistrationsStats } from '__generated__/graphql';
import { ProgressBar, StatsListContainer, StatsListFooter, StatsListRow } from './Layout';
import { RevenueColumn, RevenueColumnOption, SalesColumn, SalesColumnOption } from './TicketStats';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';

type Event = GetEventRegistrationsStats['event'];
type Ticket = Event['tickets'][0];
type BasePromotion = Ticket['promotions'][0];

interface Promotion extends BasePromotion {
  ticket: Ticket;
}

export interface TicketPromotionStatsProps {
  tickets: Ticket[];
  salesColumn: SalesColumnOption;
  revenueColumn: RevenueColumnOption;
}

const TicketPromotionStats = memo(({ tickets, salesColumn, revenueColumn } : TicketPromotionStatsProps) => {
  const { t } = useTranslation();
  const device = useMediaDevice();

  const promotions = sortBy(tickets.reduce((promotions, ticket) => [
    ...promotions,
    ...ticket.promotions.map(
      (promotion) => ({ ...promotion, ticket }),
    ),
  ], []), ['start_date', 'end_date', 'title']);

  // Group promotions by title, start date, and end date.
  // Sort each group of promotions by ticket position.
  const groupedPromotions = Object.values(groupBy(
    promotions,
    (promotion) => `${promotion.title}${promotion.start_date}${promotion.end_date}`,
  )).map((promotions: Promotion[]) => sortBy(promotions, (promotion) => promotion.ticket.position));

  const limit = 5;
  const [showingAll, setShowingAll] = useState(false);
  const visiblePromotionGroups = showingAll ? groupedPromotions : groupedPromotions.slice(0, limit);

  const totals = promotions.reduce((totals, promotion) => ({
    sold_count: totals.sold_count + promotion.sold_count,
    invitation_reservations_count: totals.invitation_reservations_count + promotion.invitation_reservations_count,
    revenue: totals.revenue + promotion.revenue,
    remaining_amount: totals.remaining_amount + promotion.remaining_amount,
    active_purchases_count: totals.active_purchases_count + promotion.active_purchases_count,
    accepted_purchases_amount: totals.accepted_purchases_amount + promotion.accepted_purchases_amount,
    capacity: totals.capacity === null || promotion.capacity === null ? null : totals.capacity + promotion.capacity,
  }), {
    sold_count: 0,
    invitation_reservations_count: 0,
    revenue: 0,
    remaining_amount: 0,
    active_purchases_count: 0,
    accepted_purchases_amount: 0,
    capacity: 0 satisfies number | null,
  });

  return (
    <>
      <StatsListContainer>
        {visiblePromotionGroups.map((promotions) => (
          <PromotionRow
            promotions={promotions}
            salesColumn={salesColumn}
            revenueColumn={revenueColumn}
            key={promotions[0].id}
          />
        ))}
        {(showingAll || groupedPromotions.length <= limit) && (
          <StatsListRow>
            <UI.Div sc={{ fontWeight: 500 }}>
              {t('totals')}
            </UI.Div>
            {!device.isSmall && <UI.Div />}
            <SalesColumn activeColumn={salesColumn} source={totals} hideCapacity />
            <RevenueColumn activeColumn={revenueColumn} source={totals} />
          </StatsListRow>
        )}
      </StatsListContainer>
      {groupedPromotions.length > limit && (
        <StatsListFooter>
          {!showingAll && (
            <UI.Button
              onClick={() => setShowingAll(true)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
                {t('show_all')}
                {' '}
              <UI.Span sc={{ muted: true }}>
                {`(${groupedPromotions.length})`}
              </UI.Span>
                {' '}
              <UI.Icon sc={{ muted: true }}>
                <ChevronDown />
              </UI.Icon>
            </UI.Button>
          )}
          {showingAll && (
            <UI.Button
              onClick={() => setShowingAll(false)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
              {t('show_less')}
              {' '}
              <UI.Icon sc={{ muted: true }}>
                <ChevronUp />
              </UI.Icon>
            </UI.Button>
          )}
        </StatsListFooter>
      )}
    </>
  );
});

interface PromotionRowProps {
  promotions: Promotion[];
  salesColumn: SalesColumnOption;
  revenueColumn: RevenueColumnOption;
}

const PromotionRow = ({ promotions, salesColumn, revenueColumn }: PromotionRowProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const device = useMediaDevice();

  const promotion = promotions[0];
  const [showingExtraStats, setShowingExtraStats] = useState(false);

  const purchasesCount = promotions.reduce((sum, promotion) => sum + promotion.sold_count, 0);
  const reservationsCount = promotions.reduce((sum, promotion) => sum + promotion.invitation_reservations_count, 0);
  const capacity = promotions.reduce((sum, promotion) => (
    sum === null ? sum : (promotion.capacity !== null ? sum + promotion.capacity : null)
  ), 0);

  const sales = {
    sold_count: purchasesCount,
    invitation_reservations_count: reservationsCount,
    capacity,
  };

  const revenue = {
    revenue: promotions.reduce((sum, promotion) => sum + promotion.revenue, 0),
    remaining_amount: promotions.reduce((sum, promotion) => sum + promotion.remaining_amount, 0),
    accepted_purchases_amount: promotions.reduce((sum, promotion) => sum + promotion.accepted_purchases_amount, 0),
    active_purchases_count: promotions.reduce((sum, promotion) => sum + promotion.active_purchases_count, 0),
  };

  const toggleExtraStats = () => setShowingExtraStats((showing) => !showing);

  const titles = uniq(promotions.map((promotion) => promotion.title).filter((title) => title));

  const startDate = promotion.start_date && formatDate(parseDate(promotion.start_date, { timezone: 'UTC' }), {
    format: 'display_compact_date',
  });

  const endDate = promotion.end_date && formatDate(parseDate(promotion.end_date, { timezone: 'UTC' }), {
    format: 'display_compact_date',
  });

  return (
    <>
      <StatsListRow>
        <UI.Div style={{ minWidth: 0 }}>
          <UI.FlexContainer>
            <UI.TooltipContainer style={{ minWidth: 0 }}>
              <UI.Truncate>
                {titles.length > 0 && (
                  <UI.Delimit>
                    {titles}
                  </UI.Delimit>
                )}
                {titles.length === 0 && t('standard')}
              </UI.Truncate>
              {(startDate || endDate) && (
                <UI.Tooltip sc={{ alignV: 'bottom', left: 20 }}>
                  {startDate && endDate && `${startDate} – ${endDate}`}
                  {startDate && !endDate && t('starts_on', { date: startDate })}
                  {!startDate && endDate && t('ends_on_date', { date: endDate })}
                </UI.Tooltip>
              )}
            </UI.TooltipContainer>
            <UI.Span>
              <UI.Button
                sc={{ blank: true, size: 'xs', my: -0.75, ml: 1 }}
                onClick={() => toggleExtraStats()}
                aria-label={t('toggle_name', { name: promotion.title || t('standard') })}
              >
                <UI.Icon>
                  {showingExtraStats && <ChevronUp />}
                  {!showingExtraStats && <ChevronDown />}
                </UI.Icon>
              </UI.Button>
            </UI.Span>
          </UI.FlexContainer>
        </UI.Div>
        {!device.isSmall && (
          <UI.Div>
            {capacity > 0 && (
              <ProgressBar
                count={purchasesCount}
                capacity={capacity}
                tooltip={t('sales_plus_reservations')}
              />
            )}
          </UI.Div>
        )}
        <SalesColumn activeColumn={salesColumn} source={sales} />
        <RevenueColumn activeColumn={revenueColumn} source={revenue} />
      </StatsListRow>
      {showingExtraStats && (
        <ExtraTicketPromotionStats
          promotions={promotions}
          salesColumn={salesColumn}
          revenueColumn={revenueColumn}
        />
      )}
    </>
  );
};

const ExtraTicketPromotionStats = ({ promotions, salesColumn, revenueColumn }: PromotionRowProps) => {
  const { t } = useTranslation();
  const device = useMediaDevice();

  return (
    <>
      {promotions.map((promotion) => (
        <StatsListRow sc={{ background: 'gray.50' }} key={promotion.id}>
          <UI.Div sc={{ pl: 4 }} style={{ minWidth: 0 }}>
            <UI.Truncate>
              <UI.Icon sc={{ mr: 1 }}>
                <UI.Icons.Ticket />
              </UI.Icon>
              {promotion.ticket.title}
            </UI.Truncate>
          </UI.Div>
          {!device.isSmall && (
            <UI.Div>
              {promotion.capacity > 0 && (
                <ProgressBar
                  count={promotion.sold_count}
                  capacity={promotion.capacity}
                  tooltip={t('sales_plus_reservations')}
                />
              )}
            </UI.Div>
          )}
          <SalesColumn activeColumn={salesColumn} source={promotion} />
          <RevenueColumn activeColumn={revenueColumn} source={promotion} />
        </StatsListRow>
      ))}
    </>
  );
};

export default TicketPromotionStats;
